import React, { useState } from 'react';
import { Space, Button } from 'antd';
import { HomeOutlined, DeleteOutlined, SaveOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Header } from './styled';
import commonSelector, { realtimePSDSelector } from '@store/common/selectors';
import { nocShareConfig } from '@components/share/utils';
import Share from '@components/share/Share';
import { DASHBOARD_TYPE, DASHBOARD_URL, PAGE_TYPE } from '@utils/constants';
import { SharingAdapter } from '@components/common/Sharing';
import { sharingSelector } from '@store/noc/sharing/selectors';
import SavingWrapper from '@components/saving/SavingWrapper/SavingWrapper';
import { ACTIONTYPES } from '@src/components/saving/utils';
import { changeSaveDashboardPromptVisibility } from '@store/dashboards/ui/actions';
import * as dashboardsUIActions from '@store/dashboards/ui/actions';
import DeleteSavedDashboardWrapper from '@components/saving/DeleteSavedDashboardWrapper/DeleteSavedDashboardWrapper';
import featureNames from '@utils/featureToggle/constants';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';
import { useDefaultHandler } from './useDefault';
import { Section } from '../../../ActionPopover/styled';
import DefaultLandingPrompt from '@components/common/DefaultLandingPage/adapter';

const { baseUrl } = window.conviva;

export type ActionsProps = {
  pageKeyForSaveService: string;
  currentShareId: number;
  pageType: number;
  isSharing: boolean;
};

function Actions({ pageKeyForSaveService, currentShareId, pageType, isSharing }: ActionsProps) {
  const shareUrl = `dashboard/real-time/sharing`;
  const savedDashboardUrl = DASHBOARD_URL[DASHBOARD_TYPE.NOC].slice(0, -1);

  const dispatch = useDispatch();
  const realtimePSDFF = useSelector(realtimePSDSelector);
  const sharingState = useSelector(sharingSelector);
  const defaultLandingPage = useDefaultLandingPageStore((state) => ({
    url: state.url,
    name: state.name,
    path: state.path,
  }));
  const { features } = useSelector(commonSelector);

  const [isShareClicked, setShareClicked] = useState(false);
  const [isSaveClicked, setSaveClicked] = useState(true);

  const { onSetDefaultLanding } = useDefaultHandler();

  const setLandingPageEnabled =
    (features[featureNames.DEFAULT_LANDING_PAGE] || true) && pageType !== PAGE_TYPE.SHARING;

  const { pathname } = window.location;
  const defaultPathname = defaultLandingPage.path || '';
  const isDefaultLandingPage = defaultPathname.endsWith(pathname);

  const disableSave = !(
    isSharing ||
    pageType === PAGE_TYPE.SHARING ||
    pageType === PAGE_TYPE.SAVED_DASHBOARD
  );

  const handleSave = () => {
    if (pageType === PAGE_TYPE.SHARING) {
      dispatch(changeSaveDashboardPromptVisibility(true, currentShareId));
    } else {
      dispatch(dashboardsUIActions.toggleUpdatePopupVisible(true, currentShareId));
    }
    setSaveClicked(!isSaveClicked);
  };

  return (
    <Section>
      <Space direction="vertical">
        <Header>Actions</Header>
        {realtimePSDFF ? (
          <Share
            config={{
              ...nocShareConfig,
              url: `${baseUrl}/${shareUrl}`,
              savedDashboardUrl: `${savedDashboardUrl}`,
            }}
            pageKeyForSaveService={pageKeyForSaveService}
            id={currentShareId}
            withIcon
          />
        ) : (
          <Button
            onClick={() => setShareClicked(true)}
            type="text"
            size="small"
            icon={<ShareAltOutlined />}
          >
            Share
          </Button>
        )}
        {realtimePSDFF ? (
          <SavingWrapper
            pageMeta={{ page: { key: pageKeyForSaveService } }}
            type={pageType === PAGE_TYPE.SAVED_DASHBOARD ? ACTIONTYPES.UPDATE : ACTIONTYPES.SAVE}
          >
            <Button type="text" size="small" icon={<SaveOutlined />}>
              {pageType === PAGE_TYPE.SAVED_DASHBOARD ? 'Update' : 'Save'}
            </Button>
          </SavingWrapper>
        ) : (
          <Button
            onClick={handleSave}
            type="text"
            size="small"
            icon={<SaveOutlined />}
            disabled={disableSave}
          >
            Save
          </Button>
        )}
        {pageType === PAGE_TYPE.SAVED_DASHBOARD ? (
          realtimePSDFF ? (
            <DeleteSavedDashboardWrapper
              pageMeta={{
                id: currentShareId,
              }}
              dashboardTypeName="NOC"
            >
              <Button type="text" size="small" icon={<DeleteOutlined />}>
                Delete
              </Button>
            </DeleteSavedDashboardWrapper>
          ) : (
            <Button
              onClick={() => dashboardsUIActions.toggleRemovePopupVisible()}
              type="text"
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          )
        ) : null}
        {setLandingPageEnabled && (
          <Button
            onClick={onSetDefaultLanding}
            type="text"
            icon={<HomeOutlined />}
            size="small"
            disabled={isDefaultLandingPage}
          >
            Make default
          </Button>
        )}
      </Space>
      <DefaultLandingPrompt />
      {isShareClicked && (
        <SharingAdapter
          isShareClicked={isShareClicked}
          config={sharingState}
          url={`${baseUrl}/dashboard/real-time/sharing`}
          setShareClicked={(v) => setShareClicked(v)}
        />
      )}
    </Section>
  );
}

export default Actions;
